import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  FormattedMessage, FormattedNumber, injectIntl, intlShape,
} from 'react-intl';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { END_DATE, START_DATE } from 'react-dates/constants';

import { EVENTS, FUNNEL_SOURCE_BOOK_DIRECT } from '../constants/analytics';
import { COLOR_BLACK, COLOR_ROOMS_RED } from '../constants/colors';
import { breakpoints } from '../constants/mediaqueries';
import { buttonStyles, fontStyles } from '../constants/styles';
import { isValidDateString } from '../helpers/dates';
import { tonightRange } from '../helpers/quickPickDates';
import { trackEvent } from '../helpers/analytics';
// Aliased: these components already have a local searchParams (URLSearchParams).
import { searchParams as searchEventParams } from '../helpers/analytics/params';
import { getUrlParams } from '../helpers/url';
import {
  BOOKING_WIDGET_CLEAR_DATES,
  BOOKING_WIDGET_UPDATE_PARAMS,
  BOOKING_WIDGET_UPDATE_PROMO_CODE,
  SEARCH_PARAMS_UPDATED,
} from '../state/actionTypes';
import { initialState } from '../state/reducers/BookingWidget';

import ErrorMessage from './ErrorMessage';
import DateField from './DirectBookingPicker/DateField';
import DateRangePopover from './DirectBookingPicker/DateRangePopover';
import InlineGuestStepper from './DirectBookingPicker/InlineGuestStepper';
import QuickPickChips from './DirectBookingPicker/QuickPickChips';


const Wrapper = styled.div`
  position: relative;
  padding: 1.5em 0 0.5em;
  ${fontStyles.bodySmall}
`;

/* Transparent bar with black hairline borders, mirroring the rooms-page
   BookingWidget (StyledWrapper) so both booking experiences read the same. */
const Bar = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1em 0;
  border-top: 1px solid ${COLOR_BLACK};
  border-bottom: 1px solid ${COLOR_BLACK};
`;

const PrimaryRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0.5rem 0;

  @media ${breakpoints.l} {
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }
`;

const DateFields = styled.div`
  display: flex;
  gap: 0.25rem;
`;

/* Per-field wrapper: scopes the popover's absolute positioning to its own DateField. */
const FieldContainer = styled.div`
  position: relative;
`;

const VerticalDivider = styled.div`
  display: none;

  @media ${breakpoints.l} {
    display: block;
    width: 1px;
    align-self: stretch;
    margin: 0.5rem 0;
    background-color: rgba(0, 0, 0, 0.2);
  }
`;

const GuestRow = styled.div`
  display: flex;
  gap: 1.25rem;
  flex-wrap: wrap;
  padding: 0 0.5rem;
`;

const SubmitButton = styled.button`
  ${buttonStyles}
  background-color: ${COLOR_BLACK};
  color: ${(props) => props.buttonColor};
  border-color: ${COLOR_BLACK};

  @media ${breakpoints.l} {
    margin-left: auto;
  }

  &[disabled] {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const SecondaryRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.5rem 0.25rem 0.25rem;

  @media ${breakpoints.l} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }
`;

const Summary = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  ${fontStyles.bodySmall}
  opacity: 0.85;
`;

const ClearButton = styled.button`
  ${fontStyles.bodySmall}
  padding: 0;
  border: 0;
  background: none;
  color: ${COLOR_BLACK};
  cursor: pointer;
`;

const SecondaryLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;

  @media ${breakpoints.l} {
    flex-direction: row;
    align-items: center;
    gap: 1.25rem;
  }
`;

const PromoForm = styled.form`
  display: flex;
  align-items: center;
  gap: 0.75rem;

  > label {
    display: flex;
    align-items: center;
  }
`;

const PromoInput = styled.input`
  margin-left: .5rem;
  padding: .25em .5em;
  border: 1px solid ${COLOR_BLACK};
  background-color: transparent;
  color: ${COLOR_BLACK};
  ${fontStyles.bodySmall}
`;

const PromoStatus = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;


class DirectBookingPicker extends Component {
  constructor(props) {
    super(props);
    const { intl: { locale } } = props;
    moment.locale(locale);

    this.state = {
      arrivalDate: null,
      departureDate: null,
      focusedInput: START_DATE,
      numAdults: 1,
      numChildren: 0,
      openButton: null,
      promoCodeInput: props.promoCode || '',
      promoCodeVisible: false,
    };

    this.datesContainerRef = React.createRef();

    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleKeyUp = this.handleKeyUp.bind(this);
    this.applyUrlParamsOrPrefill = this.applyUrlParamsOrPrefill.bind(this);
    this.openDateField = this.openDateField.bind(this);
    this.clearDates = this.clearDates.bind(this);
    this.handleDatesChange = this.handleDatesChange.bind(this);
    this.handleFocusChange = this.handleFocusChange.bind(this);
    this.incrementAdults = this.incrementAdults.bind(this);
    this.decrementAdults = this.decrementAdults.bind(this);
    this.incrementChildren = this.incrementChildren.bind(this);
    this.decrementChildren = this.decrementChildren.bind(this);
    this.handleQuickPick = this.handleQuickPick.bind(this);
    this.clearAll = this.clearAll.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.showPromoCodeField = this.showPromoCodeField.bind(this);
    this.handlePromoCodeChange = this.handlePromoCodeChange.bind(this);
    this.applyPromoCode = this.applyPromoCode.bind(this);
    this.removePromoCode = this.removePromoCode.bind(this);
  }

  componentDidMount() {
    document.body.addEventListener('keyup', this.handleKeyUp, false);
    document.body.addEventListener('mousedown', this.handleMouseDown, false);
    this.applyUrlParamsOrPrefill();
  }

  componentWillUnmount() {
    document.body.removeEventListener('keyup', this.handleKeyUp);
    document.body.removeEventListener('mousedown', this.handleMouseDown);
  }

  handleKeyUp(event) {
    if (event.keyCode === 27) {
      this.setState({ openButton: null });
    }
  }

  handleMouseDown(event) {
    if (!this.state.openButton) return;
    const datesContainer = this.datesContainerRef.current;
    if (datesContainer && datesContainer.contains(event.target)) return;
    this.setState({ openButton: null });
  }

  applyUrlParamsOrPrefill() {
    const urlParams = getUrlParams();
    const {
      arrivalDate, departureDate, numAdults, numChildren,
    } = urlParams;

    const hasArrival = isValidDateString(arrivalDate);
    const hasDeparture = isValidDateString(departureDate);
    const parsedAdults = parseInt(numAdults, 10);
    const parsedChildren = parseInt(numChildren, 10);

    if (hasArrival && hasDeparture) {
      const arr = moment(arrivalDate, moment.ISO_8601, true);
      const dep = moment(departureDate, moment.ISO_8601, true);
      // Only hydrate a positive-length range; an inverted or zero-night range
      // from a hand-edited URL falls through to the prefill below rather than
      // dispatching a negative-duration search.
      if (dep.isAfter(arr, 'day')) {
        const adults = !Number.isNaN(parsedAdults) && parsedAdults >= 1 ? parsedAdults : 1;
        const children = !Number.isNaN(parsedChildren) && parsedChildren >= 0 ? parsedChildren : 0;
        this.setState({
          arrivalDate: arr,
          departureDate: dep,
          numAdults: adults,
          numChildren: children,
        });
        this.props.updateParams({
          arrivalDate: arr.format('YYYY-MM-DD'),
          departureDate: dep.format('YYYY-MM-DD'),
          numAdults: adults,
          numChildren: children,
        });
        this.props.searchParamsUpdated({ locale: this.props.intl.locale });
        return;
      }
    }

    const { arrivalDate: prefillArrival, departureDate: prefillDeparture } = tonightRange();
    this.setState({
      arrivalDate: prefillArrival,
      departureDate: prefillDeparture,
      numAdults: 1,
      numChildren: 0,
    });
  }

  openDateField(which) {
    this.setState({ openButton: which, focusedInput: which });
    trackEvent(EVENTS.CALENDAR_OPENED, { source: FUNNEL_SOURCE_BOOK_DIRECT });
  }

  clearDates() {
    this.setState({
      arrivalDate: null,
      departureDate: null,
      focusedInput: START_DATE,
      openButton: null,
    });
  }

  handleDatesChange({ startDate, endDate }) {
    this.setState({ arrivalDate: startDate, departureDate: endDate });
  }

  handleFocusChange(input) {
    if (!input) {
      this.setState({ focusedInput: START_DATE, openButton: null });
      return;
    }
    this.setState({ focusedInput: input, openButton: input });
  }

  incrementAdults() {
    this.setState((p) => ({ numAdults: p.numAdults + 1 }));
  }

  decrementAdults() {
    this.setState((p) => ({ numAdults: Math.max(1, p.numAdults - 1) }));
  }

  incrementChildren() {
    this.setState((p) => ({ numChildren: p.numChildren + 1 }));
  }

  decrementChildren() {
    this.setState((p) => ({ numChildren: Math.max(0, p.numChildren - 1) }));
  }

  handleQuickPick(range) {
    this.setState({
      arrivalDate: range.arrivalDate,
      departureDate: range.departureDate,
    });
  }

  clearAll() {
    this.setState({
      arrivalDate: null,
      departureDate: null,
      focusedInput: START_DATE,
      numAdults: initialState.numAdults,
      numChildren: initialState.numChildren,
      openButton: null,
    });
    // Also drop the availability results and strip the search from the URL, so
    // Clear resets the whole page rather than just the fields and a reload
    // doesn't rehydrate and re-run the search that was just cleared.
    this.props.clearDates();
    const searchParams = new URLSearchParams(window.location.search);
    ['arrivalDate', 'departureDate', 'numAdults', 'numChildren'].forEach((key) => searchParams.delete(key));
    const query = searchParams.toString();
    window.history.pushState({}, '', query ? `${window.location.pathname}?${query}` : window.location.pathname);
  }

  showPromoCodeField() {
    this.setState({ promoCodeVisible: true });
  }

  handlePromoCodeChange({ target: { value } }) {
    this.setState({ promoCodeInput: value });
  }

  applyPromoCode(event) {
    event.preventDefault();
    const promoCode = this.state.promoCodeInput.trim();
    if (!promoCode) return;

    this.props.updatePromoCode({ promoCode });
    this.setState({ promoCodeVisible: false });
    if (this.props.searchInitiated) {
      this.props.searchParamsUpdated({ locale: this.props.intl.locale });
    }
  }

  removePromoCode() {
    this.props.updatePromoCode({ promoCode: '' });
    this.setState({ promoCodeInput: '', promoCodeVisible: false });
    if (this.props.searchInitiated) {
      this.props.searchParamsUpdated({ locale: this.props.intl.locale });
    }
  }

  handleSubmit() {
    const {
      arrivalDate, departureDate, numAdults, numChildren,
    } = this.state;
    if (!arrivalDate || !departureDate || numAdults < 1) return;

    const nights = departureDate.diff(arrivalDate, 'days');
    if (nights <= 0) return;

    const formatted = {
      arrivalDate: arrivalDate.format('YYYY-MM-DD'),
      departureDate: departureDate.format('YYYY-MM-DD'),
      numAdults,
      numChildren,
    };

    /* One event, not two. This used to fire the rooms-page search event as
       well as its own, so every book-direct search also inflated the rooms
       funnel. Rooms-funnel search volume drops by the book-direct share as a
       result; that is the correction, not a regression. */
    trackEvent(EVENTS.DIRECT_SEARCH_SUBMITTED, {
      ...searchEventParams({
        arrivalDate: formatted.arrivalDate,
        departureDate: formatted.departureDate,
        nights,
        numAdults,
        numChildren,
      }),
      source: FUNNEL_SOURCE_BOOK_DIRECT,
    });

    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('arrivalDate', formatted.arrivalDate);
    searchParams.set('departureDate', formatted.departureDate);
    searchParams.set('numAdults', String(numAdults));
    searchParams.set('numChildren', String(numChildren));
    window.history.pushState({}, '', `${window.location.pathname}?${searchParams.toString()}`);

    this.setState({ openButton: null });
    this.props.updateParams(formatted);
    this.props.searchParamsUpdated({ locale: this.props.intl.locale });
    this.props.onSearch();
  }

  render() {
    const {
      arrivalDate, departureDate, focusedInput, numAdults, numChildren, openButton,
      promoCodeInput, promoCodeVisible,
    } = this.state;
    const {
      blockedDays, buttonColor, intl: { formatMessage, messages }, promoCode, promoCodeValid,
      searchInitiated, searchInProgress,
    } = this.props;

    const maxGuests = parseInt(messages['booking.guestsMax'], 10) || 4;
    // Screen-reader labels come from the Book Direct Messaging record like the
    // visible ones; the defaults keep them readable if a message is missing.
    const label = (id, defaultMessage) => formatMessage({ id, defaultMessage });
    const numGuests = numAdults + numChildren;
    const nights = (arrivalDate && departureDate) ? departureDate.diff(arrivalDate, 'days') : 0;
    const canSubmit = !!arrivalDate && !!departureDate && nights > 0 && numAdults >= 1;
    const isStartOpen = openButton === START_DATE;
    const isEndOpen = openButton === END_DATE;

    const popoverProps = {
      ariaLabel: label('bookDirect.calendarLabel', 'Select your stay'),
      arrivalDate,
      blockedDays,
      departureDate,
      focusedInput,
      onClear: this.clearDates,
      onDatesChange: this.handleDatesChange,
      onFocusChange: this.handleFocusChange,
    };

    return (
      <Fragment>
        <ErrorMessage bgColor={COLOR_BLACK} color={buttonColor} />
        <Wrapper>
          <Bar>
            <PrimaryRow>
              <DateFields innerRef={this.datesContainerRef}>
                <FieldContainer>
                  <DateField
                    ariaExpanded={isStartOpen}
                    date={arrivalDate}
                    id="bookDirect-checkin"
                    isActive={isStartOpen}
                    labelDefault="Check in"
                    labelId="bookDirect.checkIn"
                    onClick={() => this.openDateField(START_DATE)}
                    placeholderDefault="Select date"
                    placeholderId="bookDirect.selectDate"
                  />
                  <If condition={isStartOpen}>
                    <DateRangePopover {...popoverProps} />
                  </If>
                </FieldContainer>
                <FieldContainer>
                  <DateField
                    ariaExpanded={isEndOpen}
                    date={departureDate}
                    id="bookDirect-checkout"
                    isActive={isEndOpen}
                    labelDefault="Check out"
                    labelId="bookDirect.checkOut"
                    onClick={() => this.openDateField(END_DATE)}
                    placeholderDefault="Select date"
                    placeholderId="bookDirect.selectDate"
                  />
                  <If condition={isEndOpen}>
                    <DateRangePopover {...popoverProps} />
                  </If>
                </FieldContainer>
              </DateFields>
              <VerticalDivider />
              <GuestRow>
                <InlineGuestStepper
                  color={COLOR_BLACK}
                  decrementAriaLabel={label('bookDirect.removeAdult', 'Remove adult')}
                  decrementDisabled={numAdults <= 1}
                  incrementAriaLabel={label('bookDirect.addAdult', 'Add adult')}
                  incrementDisabled={numAdults >= (maxGuests - numChildren)}
                  labelDefault="Adults"
                  labelId="bookDirect.guestsAdults"
                  onDecrement={this.decrementAdults}
                  onIncrement={this.incrementAdults}
                  value={numAdults}
                />
                <InlineGuestStepper
                  color={COLOR_BLACK}
                  decrementAriaLabel={label('bookDirect.removeChild', 'Remove child')}
                  decrementDisabled={numChildren <= 0}
                  incrementAriaLabel={label('bookDirect.addChild', 'Add child')}
                  incrementDisabled={numChildren >= (maxGuests - numAdults)}
                  labelDefault="Children"
                  labelId="bookDirect.guestsChildren"
                  onDecrement={this.decrementChildren}
                  onIncrement={this.incrementChildren}
                  value={numChildren}
                />
              </GuestRow>
              <SubmitButton buttonColor={buttonColor} disabled={!canSubmit} onClick={this.handleSubmit} type="button">
                <FormattedMessage defaultMessage="Find a room" id="bookDirect.findRoom" />
              </SubmitButton>
            </PrimaryRow>

            <SecondaryRow>
              <SecondaryLeft>
                <QuickPickChips
                  currentRange={arrivalDate && departureDate ? { arrivalDate, departureDate } : null}
                  onSelect={this.handleQuickPick}
                />
                <Choose>
                  {/*
                    Hidden while a search runs, as in the rooms-page widget:
                    promoCodeValid still holds the previous response until the
                    new one lands, so without this an applied code reads as
                    "not valid" for the length of the request.
                  */}
                  <When condition={!!promoCode && !promoCodeVisible && !searchInProgress}>
                    <PromoStatus>
                      <Choose>
                        <When condition={!searchInitiated}>
                          <FormattedMessage id="booking.promoCodePremature" values={{ promoCode }} />
                          <ClearButton onClick={this.removePromoCode} type="button">
                            <FormattedMessage id="booking.promoCodePrematureRemove" />
                          </ClearButton>
                        </When>
                        <When condition={!promoCodeValid}>
                          <span>×&nbsp;<FormattedMessage id="booking.promoCodeInvalid" values={{ promoCode }} /></span>
                          <ClearButton onClick={this.removePromoCode} type="button">
                            <FormattedMessage id="booking.promoCodeInvalidRemove" />
                          </ClearButton>
                        </When>
                        <Otherwise>
                          <svg fill="none" height="12" width="15" xmlns="http://www.w3.org/2000/svg"><path d="M1 6l4 5 9.5-10" stroke="#000" strokeWidth="1.25" /></svg>
                          <FormattedMessage id="booking.promoCodeValid" values={{ promoCode }} />
                          <ClearButton onClick={this.removePromoCode} type="button">
                            <FormattedMessage id="booking.promoCodeValidRemove" />
                          </ClearButton>
                        </Otherwise>
                      </Choose>
                    </PromoStatus>
                  </When>
                  <When condition={promoCodeVisible}>
                    <PromoForm onSubmit={this.applyPromoCode}>
                      {/*
                        The linter cannot recognise that `PromoInput` is an
                        input; the rule is being followed.
                      */}
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label htmlFor="bookDirect-promoCode">
                        <FormattedMessage defaultMessage="Promo Code" id="booking.promoCode" />
                        <PromoInput
                          id="bookDirect-promoCode"
                          onChange={this.handlePromoCodeChange}
                          type="text"
                          value={promoCodeInput}
                        />
                      </label>
                      <ClearButton disabled={!promoCodeInput.trim()} type="submit">
                        <FormattedMessage id="booking.inputApply" />
                      </ClearButton>
                    </PromoForm>
                  </When>
                  <Otherwise>
                    <ClearButton onClick={this.showPromoCodeField} type="button">
                      +&nbsp;<FormattedMessage defaultMessage="Promo Code" id="booking.promoCode" />
                    </ClearButton>
                  </Otherwise>
                </Choose>
              </SecondaryLeft>
              <Summary>
                <If condition={!!arrivalDate && !!departureDate}>
                  <span>
                    <FormattedMessage
                      defaultMessage="{nights, plural, one {1 night} other {# nights}} · {formattedNumGuests} {numGuests, plural, one {guest} other {guests}}"
                      id="bookDirect.nightsAndGuests"
                      values={{
                        formattedNumGuests: (<FormattedNumber value={numGuests} />),
                        nights,
                        numGuests,
                      }}
                    />
                  </span>
                </If>
                <ClearButton onClick={this.clearAll} type="button">
                  <FormattedMessage defaultMessage="Clear" id="booking.inputClear" />
                </ClearButton>
              </Summary>
            </SecondaryRow>
          </Bar>
        </Wrapper>
      </Fragment>
    );
  }
}

DirectBookingPicker.propTypes = {
  blockedDays: PropTypes.array.isRequired,
  buttonColor: PropTypes.string,
  clearDates: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
  onSearch: PropTypes.func,
  promoCode: PropTypes.string,
  promoCodeValid: PropTypes.bool.isRequired,
  searchInProgress: PropTypes.bool.isRequired,
  searchInitiated: PropTypes.bool.isRequired,
  searchParamsUpdated: PropTypes.func.isRequired,
  updateParams: PropTypes.func.isRequired,
  updatePromoCode: PropTypes.func.isRequired,
};

DirectBookingPicker.defaultProps = {
  buttonColor: COLOR_ROOMS_RED,
  onSearch: () => {},
};

const mapStateToProps = (state) => ({
  blockedDays: state.availability.blockedDays,
  promoCode: state.bookingWidget.promoCode,
  promoCodeValid: state.availability.promoCodeValid,
  searchInProgress: state.availability.searchInProgress,
  searchInitiated: state.availability.searchInitiated,
});

const mapDispatchToProps = (dispatch) => ({
  clearDates: () => dispatch({ type: BOOKING_WIDGET_CLEAR_DATES }),
  searchParamsUpdated: (payload) => dispatch({ type: SEARCH_PARAMS_UPDATED, payload }),
  updateParams: (payload) => dispatch({ type: BOOKING_WIDGET_UPDATE_PARAMS, payload }),
  updatePromoCode: (payload) => dispatch({ type: BOOKING_WIDGET_UPDATE_PROMO_CODE, payload }),
});

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(DirectBookingPicker));
